/**
 * BackButton — universal cinematic back navigation.
 *
 * Per /app/brand/originals.md — subtle glass pill, restrained gold edge,
 * NOT app-like. Sits in the top-left safe area of every guest-facing
 * experience page. One-tap exit, designed for event-night use in a dark
 * venue (≥42px tap target).
 *
 * Behaviour:
 *   1. If we've navigated within the SPA → navigate(-1)
 *   2. Else fallback to `fallbackHref` (e.g. /event/:slug, /dashboard)
 *   3. NEVER strands the guest on a dead route
 *
 * Props:
 *   fallbackHref — string, route used when history is empty
 *   fallback     — alias for fallbackHref (legacy callers)
 *   label        — string, button copy (default "Back")
 *   variant      — 'default' (gold edge) | 'subtle' | 'on-light' | 'legacy'
 *                  'legacy' = the prior bare-text style, used by older
 *                  callers that haven't migrated yet
 *   className    — extra Tailwind classes (legacy callers)
 *   testId       — data-testid (default 'back-btn'; legacy = 'back-button')
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { CaretLeft, ArrowLeft } from '@phosphor-icons/react';
import { resolveSmartBackTarget } from '../shared/navigation/useSmartBack';

const VARIANT = {
  default: {
    bg: 'rgba(18,18,28,0.82)',
    border: '1px solid rgba(212,175,94,0.18)',
    color: 'rgba(255,255,255,0.92)',
    hoverShadow: '0 8px 28px rgba(212,175,94,0.18)',
  },
  subtle: {
    bg: 'rgba(18,18,28,0.6)',
    border: '1px solid rgba(255,255,255,0.06)',
    color: 'rgba(255,255,255,0.75)',
    hoverShadow: '0 6px 20px rgba(0,0,0,0.4)',
  },
  'on-light': {
    bg: 'rgba(255,255,255,0.88)',
    border: '1px solid rgba(0,0,0,0.08)',
    color: 'rgba(20,20,30,0.86)',
    hoverShadow: '0 8px 24px rgba(0,0,0,0.12)',
  },
};

const resolveTarget = () => {
  if (typeof window === 'undefined') return false;
  // history.length > 1 isn't enough — a fresh tab landing directly on a
  // URL still reports >1 due to browser internals. We cross-check with a
  // session-scoped flag set on first SPA-internal navigation (mounted by
  // EventSessionProvider's popstate listener).
  return window.history.length > 1 &&
    window.sessionStorage.getItem('eventdrop_spa_navigated') === '1';
};

/**
 * iter186 + iter196 — Guest-session-aware fallback resolver.
 * Delegates to the canonical `resolveSmartBackTarget` so every
 * BackButton across the app uses the same priority order:
 *
 *   booth_os entry → /booth-os/:event_id
 *   access entry   → /access/:slug
 *   personal-link  → /event/:slug/g/:guest_id
 *   no session     → caller's fallbackHref
 *
 * Pages that pass an explicit `fallbackHref` keep it as the
 * last-resort branch — the smart resolver only overrides when a
 * valid guest session exists.
 */
const guestSessionFallback = (currentFallback) => {
  return resolveSmartBackTarget({ fallbackHref: currentFallback });
};

export default function BackButton({
  fallbackHref,
  fallback,
  label = 'Back',
  variant = 'default',
  className = '',
  testId,
}) {
  const navigate = useNavigate();
  const target = guestSessionFallback(fallbackHref || fallback || '/');
  const onClick = () => {
    if (resolveTarget()) navigate(-1);
    else navigate(target);
  };

  // Legacy variant — keeps the bare text-only style some older pages
  // (BoothPage, ChallengePage) used before this upgrade. Tap target is
  // still hit-friendly thanks to py-2 from callers.
  if (variant === 'legacy') {
    return (
      <button
        type="button"
        onClick={onClick}
        className={`inline-flex items-center gap-1.5 text-white/55 hover:text-white text-xs font-bold transition-colors ${className}`}
        data-testid={testId || 'back-button'}
        aria-label={label}
      >
        <ArrowLeft size={14} weight="bold" />
        {label}
      </button>
    );
  }

  const v = VARIANT[variant] || VARIANT.default;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId || 'back-btn'}
      aria-label={label}
      className={className}
      style={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        height: 42,
        padding: '0 16px 0 12px',
        borderRadius: 999,
        background: v.bg,
        border: v.border,
        color: v.color,
        fontSize: 13,
        fontWeight: 600,
        letterSpacing: '0.01em',
        cursor: 'pointer',
        backdropFilter: 'blur(18px)',
        WebkitBackdropFilter: 'blur(18px)',
        transition: 'transform 0.2s cubic-bezier(0.16,1,0.3,1), box-shadow 0.2s ease',
        boxShadow: '0 2px 10px rgba(0,0,0,0.25)',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-1px)';
        e.currentTarget.style.boxShadow = v.hoverShadow;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 2px 10px rgba(0,0,0,0.25)';
      }}
    >
      <CaretLeft size={16} weight="bold" />
      <span>{label}</span>
    </button>
  );
}

export { BackButton };
